import { http, unwrapData } from '@noor/api';
import { env } from '@noor/config';
import { OPERATOR_MAX_PAGES, OPERATOR_PAGE_SIZE } from '../config/constants';
import type { ChatListFilter } from '../lib/list-filter';
import {
  chatCourierSchema,
  chatListSchema,
  chatMacrosSchema,
  chatMessagesSchema,
  chatNotesSchema,
  chatOperatorListSchema,
  chatPresenceSchema,
  chatSchema,
  type Chat,
  type ChatCourier,
  type ChatList,
  type ChatMacro,
  type ChatMessages,
  type ChatNote,
  type ChatOperator,
  type ChatPresence,
  type ConversationId,
  type MacroLang,
} from '../model/schema';

/**
 * API for the support-inbox `conversation` domain — the main-service chat
 * contract cpanel's `features/chat/api/chat.endpoints.ts` spoke (`…/api/v1`).
 * Every read PARSES its response with a Zod schema (contract drift fails at the
 * boundary); every write returns void — callers invalidate through
 * `conversationsKeys`.
 */

/** The courier snapshot lives behind the main gateway host. */
const coreApiOptions = { baseURL: `${env.VITE_CORE_API_URL}/api/v1` };

export const conversationsApi = {
  /** `GET chats` — one page of the chat list for a filter. */
  async list(filter: ChatListFilter): Promise<ChatList> {
    const raw = await http.get<unknown>('chats', { query: { ...filter } });
    return chatListSchema.parse(raw);
  },

  /** `GET chats/{id}` — one chat (body is the chat itself). */
  async getById(id: ConversationId): Promise<Chat> {
    const raw = await http.get<unknown>(`chats/${id}`);
    return chatSchema.parse(raw);
  },

  /**
   * `GET chats/{id}/messages` — the newest `limit` messages (skip 0). The
   * thread grows by widening this window rather than paging by offset, so a
   * message arriving mid-scroll can never shift a page boundary.
   */
  async messages(id: ConversationId, limit: number): Promise<ChatMessages> {
    const raw = await http.get<unknown>(`chats/${id}/messages`, {
      query: { skip: 0, limit },
    });
    return chatMessagesSchema.parse(raw);
  },

  /**
   * `POST chats/{id}/messages` (multipart). A file-only send carries the `img`
   * body cpanel used, since the backend requires a non-empty message.
   */
  async sendMessage(
    id: ConversationId,
    body: { message: string; files: File[] },
  ): Promise<void> {
    const form = new FormData();
    form.append('message', body.message);
    for (const file of body.files) form.append('files', file);
    await http.post<unknown>(`chats/${id}/messages`, form);
  },

  /** `POST chats/{id}/acquire` — take the chat as the responsible operator. */
  async acquire(id: ConversationId): Promise<void> {
    await http.post<unknown>(`chats/${id}/acquire`);
  },

  /** `PATCH chats/{id}` — close with status 3 (resolved) or 4 (not resolved). */
  async setStatus(id: ConversationId, status: number): Promise<void> {
    await http.patch<unknown>(`chats/${id}`, { status });
  },

  /** `PATCH chats/{id}/move` — hand the chat over to another operator. */
  async moveTo(id: ConversationId, userId: number | string): Promise<void> {
    await http.patch<unknown>(`chats/${id}/move`, {
      user_id: Number(userId),
    });
  },

  /** `POST chats/{id}/confirm-move` — accept / reject an incoming hand-over. */
  async confirmMove(id: ConversationId, confirm: boolean): Promise<void> {
    await http.post<unknown>(`chats/${id}/confirm-move`, { confirm });
  },

  /** `POST chats/{id}/seen` — mark the thread read. */
  async markSeen(id: ConversationId): Promise<void> {
    await http.post<unknown>(`chats/${id}/seen`, {});
  },

  /** `GET chat/{id}/notes` — operator-only notes. */
  async notes(id: ConversationId): Promise<ChatNote[]> {
    const raw = await http.get<unknown>(`chat/${id}/notes`);
    return chatNotesSchema.parse(unwrapData(raw) ?? []);
  },

  /** `POST chat/{id}/notes`. */
  async createNote(id: ConversationId, text: string): Promise<void> {
    await http.post<unknown>(`chat/${id}/notes`, { text });
  },

  /** `DELETE chat/{id}/notes/{noteId}`. */
  async deleteNote(id: ConversationId, noteId: ConversationId): Promise<void> {
    await http.delete<unknown>(`chat/${id}/notes/${noteId}`);
  },

  /** `GET chat/macros` — templates in one language (sent as `Accept-Language`). */
  async macros(lang: MacroLang): Promise<ChatMacro[]> {
    const raw = await http.get<unknown>('chat/macros', {
      headers: { 'Accept-Language': lang },
    });
    return chatMacrosSchema.parse(unwrapData(raw) ?? []);
  },

  /** `POST chat/macros`. */
  async createMacro(body: {
    title: string;
    message: string;
    lang: MacroLang;
  }): Promise<void> {
    await http.post<unknown>('chat/macros', body);
  },

  /** `PATCH chat/macros/{id}`. */
  async updateMacro(
    id: ConversationId,
    body: { title: string; message: string; lang: MacroLang },
  ): Promise<void> {
    await http.patch<unknown>(`chat/macros/${id}`, body);
  },

  /** `DELETE chat/macros/{id}`. */
  async deleteMacro(id: ConversationId): Promise<void> {
    await http.delete<unknown>(`chat/macros/${id}`);
  },

  /** `GET /ws/users/{id}/check-online?role=` — presence of the chat owner. */
  async presence(
    userId: ConversationId,
    role: string | undefined,
  ): Promise<ChatPresence> {
    const raw = await http.get<unknown>(`/ws/users/${userId}/check-online`, {
      query: role ? { role } : {},
    });
    return chatPresenceSchema.parse(raw);
  },

  /** `GET admin/couriers/{id}` — the courier snapshot for the side panel. */
  async courier(id: ConversationId): Promise<ChatCourier> {
    const raw = await http.get<unknown>(`admin/couriers/${id}`, coreApiOptions);
    return chatCourierSchema.parse(unwrapData(raw));
  },

  /**
   * Call-center operators a chat can be handed to. cpanel listed
   * `GET admin/users?limit=100&skip=` and kept the CALL_CENTER role client-side;
   * pages are walked until the reported `count` is exhausted.
   */
  async operators(): Promise<ChatOperator[]> {
    const operators: ChatOperator[] = [];
    for (let page = 0; page < OPERATOR_MAX_PAGES; page += 1) {
      const raw = await http.get<unknown>('admin/users', {
        ...coreApiOptions,
        query: { limit: OPERATOR_PAGE_SIZE, skip: page * OPERATOR_PAGE_SIZE },
      });
      const parsed = chatOperatorListSchema.parse(raw);
      operators.push(
        ...parsed.data.filter(
          (user) => String(user.role).toUpperCase() === 'CALL_CENTER',
        ),
      );
      if ((page + 1) * OPERATOR_PAGE_SIZE >= parsed.count || parsed.data.length === 0)
        break;
    }
    return operators;
  },

  /**
   * The active courier chat of an order, if any (cpanel `useChatList`:
   * `GET chats?skip=0&limit=2&type=COURIER&active=true&order_id=…`). Resolves
   * the first chat's id.
   */
  async findOrderCourierChat(orderId: string | number): Promise<string | undefined> {
    const raw = await http.get<unknown>('chats', {
      query: {
        skip: '0',
        limit: '2',
        type: 'COURIER',
        active: 'true',
        order_id: String(orderId),
      },
    });
    const list: unknown[] = isRecord(raw) && Array.isArray(raw.data) ? raw.data : [];
    return readId(list[0]);
  },

  /**
   * Open a courier chat for an order (cpanel `useCreateChat`:
   * `POST chats {role:'COURIER', order_id, subject:'No message'}`). Resolves
   * the new chat's id.
   */
  async createOrderCourierChat(orderId: string | number): Promise<string | undefined> {
    const raw = await http.post<unknown>('chats', {
      role: 'COURIER',
      order_id: orderId,
      subject: 'No message',
    });
    return readId(isRecord(raw) && isRecord(raw.data) ? raw.data : raw);
  },
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readId(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;
  return typeof value.id === 'string' || typeof value.id === 'number'
    ? String(value.id)
    : undefined;
}
